import {
  getAppBaseDomain,
  getLoginUrl,
  isLoginDomain,
  isPublicAuthPath,
} from "./environment";
import { PROJECT_SWITCH_LANDING_PATH } from "./workspaceSwitch/projectSwitchLanding";

/** Same hub the project switcher uses — exists in every app before fetchUser. */
export const POST_LOGIN_LANDING_PATH = PROJECT_SWITCH_LANDING_PATH;

const APP_SUBDOMAINS = new Set([
  "tax",
  "epr",
  "invoice",
  "tax-agent",
  "omp",
  "authorize",
]);

function isLocalhostHostName(hostname) {
  return (
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname === "[::1]" ||
    /\.local$/.test(hostname || "")
  );
}

/**
 * Keep a post-login return URL only if it is our product, an app host
 * (never login/verify), and not a public auth path. `/` becomes the settings hub
 * so auth routes cannot bounce `/` → `/login` before fetchUser.
 */
export function sanitizePostLoginRedirectUrl(redirectUrl) {
  if (!redirectUrl || typeof redirectUrl !== "string") return null;

  try {
    const currentBase = getAppBaseDomain();
    if (!currentBase) return null;

    const parsed = new URL(redirectUrl, window.location.origin);
    if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
      return null;
    }

    const parts = parsed.hostname.split(".");
    if (parts.length < 2) return null;

    const subdomain = parts[0].toLowerCase();
    const redirectBase = parts.slice(1).join(".");
    if (redirectBase.toLowerCase() !== currentBase.toLowerCase()) {
      return null;
    }
    if (!APP_SUBDOMAINS.has(subdomain)) {
      return null;
    }

    let pathname = parsed.pathname || "/";
    if (pathname === "/" || pathname === "" || isPublicAuthPath(pathname)) {
      pathname = POST_LOGIN_LANDING_PATH;
    }
    parsed.pathname = pathname;
    parsed.searchParams.delete("fromLogin");
    parsed.searchParams.delete("redirectUrl");
    parsed.hash = "";
    return parsed.toString();
  } catch {
    return null;
  }
}

export function appendRedirectUrlToLoginUrl(loginUrl, returnTo) {
  const sanitized = sanitizePostLoginRedirectUrl(returnTo);
  if (!sanitized || !loginUrl) return loginUrl;

  try {
    const parsed = new URL(loginUrl, window.location.origin);
    parsed.searchParams.set("redirectUrl", sanitized);
    if (String(loginUrl).startsWith("http")) {
      return parsed.toString();
    }
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return loginUrl;
  }
}

const STASH_KEY = "lappa_post_login_redirect";

/** Persist the login-page return URL across Google/Xero (query is lost on complete). */
export function stashReturnUrlFromCurrentLoginPage() {
  if (typeof window === "undefined") return;
  const fromQuery = sanitizePostLoginRedirectUrl(
    new URLSearchParams(window.location.search).get("redirectUrl")
  );
  if (!fromQuery) return;
  try {
    sessionStorage.setItem(STASH_KEY, fromQuery);
  } catch {
    /* ignore */
  }
}

export function consumeStashedPostLoginRedirectUrl() {
  if (typeof window === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(STASH_KEY);
    sessionStorage.removeItem(STASH_KEY);
    return sanitizePostLoginRedirectUrl(raw);
  } catch {
    return null;
  }
}

/**
 * Login URL that remembers the current app (Authorize vs Tax) without
 * sending `/` back as the landing path.
 */
export function getLoginUrlWithReturn(returnTo) {
  const loginUrl = getLoginUrl();
  if (typeof window === "undefined") return loginUrl;
  if (isLoginDomain()) return loginUrl;
  if (isLocalhostHostName(window.location.hostname)) return loginUrl;

  const href =
    returnTo ||
    `${window.location.origin}${window.location.pathname}${window.location.search}`;
  return appendRedirectUrlToLoginUrl(loginUrl, href);
}
